import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Plus, Minus } from 'lucide-react';

const FAQS = {
  en: [
    { q: 'Is this safe for my PC?', a: 'Absolutely. Every optimization is thoroughly tested on multiple Windows 10/11 systems. All changes are reversible — you can undo everything with a single click.' },
    { q: 'How does the purchase work?', a: 'All purchases are handled through our Discord server. After joining, you\'ll get instant access to the files and a dedicated support channel.' },
    { q: 'Does this work for all games?', a: 'Yes. Vanta optimizes your Windows system globally, so every game benefits — from CS2 to Valorant to Warzone and beyond.' },
    { q: 'Do I need technical knowledge?', a: 'Not at all. The installation guide is step-by-step and takes about 5 minutes. If you get stuck, our Discord support team is there to help.' },
    { q: 'What\'s the difference between V1 and V2?', a: 'V1 includes 25+ core optimizations ideal for most players. V2 adds 50+ premium tweaks, a full macro suite, priority support, and lifetime updates.' },
    { q: 'Can I use this on multiple PCs?', a: 'Each purchase is for one PC. For additional devices, you\'ll need a separate license. Contact us on Discord for bundle deals.' },
  ],
  de: [
    { q: 'Ist das sicher für meinen PC?', a: 'Absolut. Jede Optimierung wurde auf mehreren Windows-Systemen getestet. Alle Änderungen sind reversibel — du kannst alles mit einem Klick rückgängig machen.' },
    { q: 'Wie funktioniert der Kauf?', a: 'Alle Käufe werden über unseren Discord-Server abgewickelt. Nach dem Beitreten erhältst du sofort Zugang zu den Dateien und einem Support-Kanal.' },
    { q: 'Funktioniert das für alle Spiele?', a: 'Ja. Vanta optimiert dein Windows-System global, sodass jedes Spiel profitiert — von CS2 bis Valorant bis Warzone.' },
    { q: 'Brauche ich technisches Wissen?', a: 'Überhaupt nicht. Die Installationsanleitung ist Schritt-für-Schritt und dauert ca. 5 Minuten. Unser Discord-Team hilft dir, falls du nicht weiterkommst.' },
    { q: 'Was ist der Unterschied zwischen V1 und V2?', a: 'V1 enthält 25+ Kern-Optimierungen, ideal für die meisten Spieler. V2 fügt 50+ Premium-Tweaks, ein Makro-Suite, Priority-Support und lebenslange Updates hinzu.' },
    { q: 'Kann ich das auf mehreren PCs nutzen?', a: 'Jeder Kauf gilt für einen PC. Für weitere Geräte benötigst du eine separate Lizenz. Kontaktiere uns auf Discord für Bundle-Deals.' },
  ],
};

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      data-testid={`faq-item-${index}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.5 }}
      className="border-b border-white/8 last:border-none"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="font-outfit text-base font-light text-white group-hover:text-white/90 transition-colors pr-4">
          {faq.q}
        </span>
        <motion.div
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 w-6 h-6 rounded-full border border-white/15 flex items-center justify-center"
        >
          <Plus size={12} className="text-zinc-400" />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="font-inter text-sm text-zinc-500 leading-relaxed pb-5 pr-10">
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQSection() {
  const { lang } = useLanguage();
  const faqs = FAQS[lang] || FAQS.en;

  return (
    <section id="faq" className="py-40 relative overflow-hidden">
      <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/25 to-transparent origin-top"
      />

      <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }} viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-inter text-xs tracking-[0.3em] text-zinc-700 uppercase mb-4">FAQ</p>
          <h2 className="font-outfit text-4xl sm:text-5xl font-light tracking-tight text-white">
            {lang === 'de' ? 'Häufige Fragen' : 'Common Questions'}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-3xl border border-white/10 backdrop-blur-2xl"
          style={{ background: 'rgba(255,255,255,0.02)' }}
        >
          {faqs.map((faq, i) => <FAQItem key={i} faq={faq} index={i} />)}
        </motion.div>
      </div>
    </section>
  );
}
